// OperationBenefitsAddEditModal.js
import React, { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from '@mui/icons-material/Lock';
import { DateInput, Input } from "../../../../../common/Input";
import { Select } from "../../../../../common/Select";
import { useEnumsApi } from "../../../../hooks/useEnumsApi";
import { ConfirmationModal } from "../../../../../common/Modal";

const initialFormState = {
  effectiveDate: null,
  rate: 0,
  operationBenefitGroupId: "",
  maintenanceSalaryCombinationV2Id: ""
};

const OperationBenefitsAddEditModal = ({
  isOpen,
  onClose,
  selectedItem,
  selectOptions = [],
  loading,
  onSave,
  mode = "Add",
  rules = []
}) => {
  const { maintenanceTypes, fetchMaintenanceData } = useEnumsApi();
  const [valueLists, setValueLists] = useState({});
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(initialFormState);

  // Confirmation state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Convert rules into selectable options for the dropdown
  const rateCategoryOptions = useMemo(() => {
    return rules.map(r => ({
      value: r.id,
      label: r.maintenanceSalaryCombinationCodeCode || `Rule ID: ${r.id}`
    }));
  }, [rules]);

  // Find selected rule to get its details criteria
  const selectedRule = useMemo(() => {
    if (!form.maintenanceSalaryCombinationV2Id) return null;
    return rules.find(r => r.id.toString() === form.maintenanceSalaryCombinationV2Id.toString());
  }, [form.maintenanceSalaryCombinationV2Id, rules]);

  // Find benefit group label for summary
  const selectedGroupLabel = useMemo(() => {
    const group = selectOptions.find(o => o.value?.toString() === form.operationBenefitGroupId?.toString());
    return group ? group.label : "N/A";
  }, [form.operationBenefitGroupId, selectOptions]);

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        setForm({
          ...selectedItem,
          effectiveDate: selectedItem.effectiveDate ?? null,
          rate: selectedItem.rate ?? 0,
          operationBenefitGroupId: selectedItem.operationBenefitGroupId ?? "",
          maintenanceSalaryCombinationV2Id: selectedItem.maintenanceSalaryCombinationV2Id ?? ""
        });
      } else {
        setForm(initialFormState);
      }
      setErrors({});
      setShowConfirmModal(false);
    }
  }, [isOpen, selectedItem]);

  // Fetch detail labels when rule selection changes
  useEffect(() => {
    if (selectedRule?.details?.length > 0) {
      const typeIds = selectedRule.details.map(d => d.maintenanceTypeId);
      loadValuesForTypes(typeIds);
    }
  }, [selectedRule]);

  const loadValuesForTypes = async (typeIds) => {
    const missingIds = typeIds.filter(id => !valueLists[id]);
    if (missingIds.length === 0) return;

    const data = await fetchMaintenanceData(missingIds);
    if (data) {
      setValueLists(prev => ({ ...prev, ...data }));
    }
  };

  const getLookupText = (typeId, valueId) => {
    const type = maintenanceTypes.find(t => t.value?.toString() === typeId?.toString());
    const list = valueLists[typeId?.toString()] || [];
    const item = list.find(v => {
      const compareId = v.value !== undefined ? v.value : v.id;
      return compareId?.toString() === valueId?.toString();
    });

    return {
      typeName: type ? type.name : "Unknown",
      valueName: item ? (item.label || "N/A") : "Loading..."
    };
  };

  const validate = () => {
    const e = {};
    const rateValue = parseFloat(form.rate);

    if (form.effectiveDate === "" || form.effectiveDate === null)
      e.effectiveDate = "Effective Date is required";
    if (!form.maintenanceSalaryCombinationV2Id)
      e.rateHeader = "Target Rate Category is required";
    if (!form.operationBenefitGroupId)
      e.group = "Benefit Group is required";
    if (form.rate === null || form.rate === undefined || form.rate === "") {
      e.rate = "Rate is required";
    } else if (isNaN(rateValue) || rateValue < 0) {
      e.rate = "Rate must be a valid non-negative number";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveClick = () => {
    if (validate()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    onSave(form);
  };

  // Render readable HTML/JSX message for the Confirmation Modal
  const renderConfirmationMessage = () => {
    const formattedDate = form.effectiveDate
      ? new Date(form.effectiveDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      : "N/A";

    const codeLabel = selectedRule?.maintenanceSalaryCombinationCodeCode || "N/A";

    return (
      <div className="space-y-3 text-left text-xs">
        <p className="text-gray-600 font-medium text-sm">
          Are you sure you want to {mode === "Add" ? "create" : "update"} this Operation Benefit?
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-2">
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Effective Date</span>
              <span className="font-bold text-gray-800 text-sm">{formattedDate}</span>
            </div>
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Benefit Rate</span>
              <span className="font-bold text-green-600 text-sm">{Number(form.rate).toFixed(2)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-2">
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Salary Code</span>
              <span className="font-semibold text-gray-700">{codeLabel}</span>
            </div>
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Benefit Group</span>
              <span className="font-semibold text-gray-700">{selectedGroupLabel}</span>
            </div>
          </div>

          <div>
            <span className="text-gray-400 block font-semibold uppercase text-[10px] mb-1">Filter Tagging</span>
            <div className="flex flex-wrap gap-1">
              {selectedRule?.details && selectedRule.details.length > 0 ? (
                selectedRule.details.map((detail, idx) => {
                  const lookups = getLookupText(detail.maintenanceTypeId, detail.maintenanceValueId);
                  return (
                    <span key={idx} className="bg-blue-100 text-blue-800 text-[10px] px-2 py-0.5 rounded font-medium">
                      {lookups.typeName}: <strong>{lookups.valueName}</strong>
                    </span>
                  );
                })
              ) : (
                <span className="text-gray-400 italic">No specific criteria tagged</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[90vh]">

          {/* Header */}
          <div className="px-6 py-4 border-b flex justify-between items-center rounded-t-lg bg-gray-100/50">
            <h2 className="text-xl font-bold">{mode === "Add" ? "Create" : "Update"} Operation Benefit</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors border-none bg-transparent cursor-pointer">
              <CloseIcon className="text-gray-500" />
            </button>
          </div>

          <div className="p-8 overflow-y-auto space-y-8">

            {/* Section 1: Applied Matching Criteria */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-600">
                <LockIcon fontSize="small" />
                <h3 className="font-bold text-sm uppercase tracking-wider">Applied Matching Criteria</h3>
              </div>

              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 grid grid-cols-2 gap-y-4 gap-x-6">
                {selectedRule?.details && selectedRule.details.length > 0 ? (
                  selectedRule.details.map((detail, index) => {
                    const lookups = getLookupText(detail.maintenanceTypeId, detail.maintenanceValueId);
                    return (
                      <div key={index} className="flex flex-col border-l-2 border-blue-200 pl-3">
                        <span className="text-[10px] font-bold text-blue-500 uppercase">{lookups.typeName}</span>
                        <span className="text-sm font-semibold text-gray-700">{lookups.valueName}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-sm text-gray-400 italic">
                    {form.maintenanceSalaryCombinationV2Id
                      ? "No specific filter criteria tagged to this rate."
                      : "Select a Target Rate Category below to view applied criteria."}
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Date Configuration */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-700">
                <h3 className="font-bold text-sm uppercase tracking-wider">Benefit Config Date/s</h3>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {selectedItem?.createdAt != null && (
                  <DateInput
                    label="Transaction Date:"
                    value={selectedItem.createdAt}
                    isDisabled={true}
                  />
                )}

                <DateInput
                  label="Effective Date:"
                  value={form.effectiveDate}
                  onChange={e => setForm({ ...form, effectiveDate: e.target.value })}
                  isRequired={true}
                  isViewOnly={mode === "Edit"}
                  error={!!errors.effectiveDate}
                  errorMessage={errors.effectiveDate}
                />
              </div>
            </div>

            {/* Section 3: Benefit Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-700">
                <h3 className="font-bold text-sm uppercase tracking-wider">Benefit Details</h3>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <Select
                  label="Apply to Rate Category"
                  placeholder="-- Select Rate --"
                  options={rateCategoryOptions}
                  value={form.maintenanceSalaryCombinationV2Id}
                  onChange={(e) => setForm({ ...form, maintenanceSalaryCombinationV2Id: e.target.value })}
                  error={!!errors.rateHeader}
                  errorMessage={errors.rateHeader}
                  disabled={mode === "Edit"}
                />

                <Select
                  label="Benefit Group"
                  placeholder={loading ? "Loading..." : "-- Select Group --"}
                  options={selectOptions}
                  value={form.operationBenefitGroupId}
                  onChange={(e) => setForm({ ...form, operationBenefitGroupId: e.target.value })}
                  error={!!errors.group}
                  errorMessage={errors.group}
                  disabled={loading}
                />

                <Input
                  label="Rate Value"
                  type="number"
                  placeholder="0.00"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  error={!!errors.rate}
                  errorMessage={errors.rate}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t flex justify-end gap-3 rounded-b-lg bg-gray-100/50">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded bg-white font-bold text-gray-500 cursor-pointer hover:bg-gray-100"
            >
              CANCEL
            </button>
            <button
              onClick={handleSaveClick}
              className="px-6 py-2 bg-blue-600 text-white rounded font-bold cursor-pointer border-none hover:bg-blue-700"
            >
              SAVE BENEFIT
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        title="Confirm Operation Benefit"
        message={renderConfirmationMessage()}
        confirmText="Yes, Save Benefit"
        cancelText="Cancel"
        type="info"
      />
    </>
  );
};

export default OperationBenefitsAddEditModal;